import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../services/firebase";
import { useAuth } from "../../contexts/AuthContext";
import Footer from "./Footer";
import ContactForm from "./ContactForm";
import RegisterInterestForm from "./RegisterInterestForm";
import LoginModal from "../Auth/LoginModal";
import OfferModal from "./OfferModal";
import LocationSeo from "./locationSeo";
import { getLocationById } from "../../config/locations";
import "../../styles/modern-landing.css";

const DAYS = ["Mon", "Tues", "Wed", "Thurs", "Fri", "Sat"];

// Default schedule matching the original hardcoded timetable
const DEFAULT_SCHEDULE = {
	Mon: [
		{ time: "10:00", note: "" },
		{ time: "13:00", note: "" },
		{ time: "17:30", note: "" },
		{ time: "18:45", note: "" },
		{ time: "20:00", note: "" },
	],
	Tues: [
		{ time: "09:00", note: "PT only" },
		{ time: "10:00", note: "" },
		{ time: "17:30", note: "" },
		{ time: "18:45", note: "" },
		{ time: "20:00", note: "" },
	],
	Wed: [
		{ time: "10:00", note: "" },
		{ time: "11:00", note: "PT only" },
		{ time: "12:00", note: "" },
		{ time: "17:30", note: "" },
		{ time: "18:45", note: "" },
	],
	Thurs: [
		{ time: "10:00", note: "" },
		{ time: "17:30", note: "" },
		{ time: "18:45", note: "" },
		{ time: "20:00", note: "" },
	],
	Fri: [
		{ time: "11:00", note: "PT only" },
		{ time: "12:00", note: "" },
		{ time: "13:00", note: "" },
		{ time: "18:00", note: "" },
	],
	Sat: [
		{ time: "08:30", note: "" },
		{ time: "10:30", note: "" },
	],
};

// LandingPage is the full rich Sidcup page. It is config-fed for SEO, hero
// copy, address and JSON-LD via the location config (defaults to Sidcup), while
// keeping its rich sections (testimonials, video modal, live Firestore
// schedule, stats). Future locations can adopt this layout by passing a config
// with the same shape.
const LandingPage = ({ location }) => {
	const loc = location || getLocationById("sidcup");
	const hero = loc?.hero || {};

	// Timetable day columns are config-driven so a location (e.g. Ebbsfleet)
	// can include a Sunday column without affecting Sidcup (Mon–Sat).
	const days = loc?.timetable?.days || DAYS;

	// Enquiry form selection:
	//   'closer'            -> LeadConnector iframe (Sidcup)
	//   'register-interest' -> simple interest form
	//   'enquiry'           -> EmailJS-backed "Get Started" form, location tagged
	const enquiryType = loc?.enquiryType || "closer";

	const [showLogin, setShowLogin] = useState(false);
	const [showOffer, setShowOffer] = useState(false);
	const [schedule, setSchedule] = useState(null);
	const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
	const [activeVideo, setActiveVideo] = useState(null);
	const [showScrollTop, setShowScrollTop] = useState(false);
	const { user, logout } = useAuth();

	const handleLogout = async () => {
		try {
			await logout();
		} catch (error) {
			console.error("Logout error:", error);
		}
	};

	// Fetch published schedule from Firestore
	useEffect(() => {
		const fetchSchedule = async () => {
			try {
				const scheduleDoc = await getDoc(doc(db, "config", "schedule"));
				if (scheduleDoc.exists()) {
					setSchedule(scheduleDoc.data());
				}
			} catch (err) {
				console.error("Error loading schedule:", err);
			}
		};
		fetchSchedule();
	}, []);

	// Show/hide scroll to top button based on scroll position
	React.useEffect(() => {
		const handleScroll = () => {
			if (window.scrollY > 300) {
				setShowScrollTop(true);
			} else {
				setShowScrollTop(false);
			}
		};

		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	const scrollToTop = () => {
		window.scrollTo({
			top: 0,
			behavior: "smooth",
		});
	};

	const categories = [
		"Personal Training",
		"Strength",
		"Group Classes",
		"Swimming",
		"Cardio Equipment",
		"Functional Workouts",
	];

	const features = [
		{
			image: "Strength01.png",
			title: "Training",
			subtitle: "Structured Coaching. Built Around You.",
			description:
				"Every session is coach-led and structured to help you progress safely and effectively. With small groups of up to six, you still receive individual coaching with exercises tailored to your level. It's a private training space designed for people who want to train properly, without the chaos of a busy gym floor.",
		},
		{
			image: "Confidence01.png",
			title: "Community",
			subtitle: "Train With People Like You.",
			description:
				"Be part of a supportive, motivating environment where everyone is working towards the same goal of getting better. Training alongside like-minded people keeps you motivated and consistent. We also bring people together outside of sessions through community events, building something bigger than just workouts.",
		},
		{
			image: "Energy01.png",
			title: "Accountability",
			subtitle: "Consistency That Actually Sticks.",
			description:
				"With sessions booked into your diary, training becomes part of your routine, not something you skip. You'll have ongoing support from your coaches to stay on track even when motivation dips, pushing you when needed so you keep showing up and making progress.",
		},
		{
			image: "Sophie01.png",
			title: "Education",
			subtitle: "Understand Your Training.",
			description:
				"We don't just tell you what to do, we help you understand why. You'll learn proper technique, movement patterns, and how to train effectively, giving you confidence inside and outside the gym. We also run seminars throughout the year covering training, nutrition, and lifestyle.",
		},
	];

	const testimonials = [
		{
			image: "Charlotte.png",
			video: "CharlotteTestimonial01.mp4",
			name: "Charlotte",
			memberSince: "2025",
			quote:
				"So the biggest benefit of being a member of Urban 59 has definitely been gaining fitness and strength, and finding a community of people who are similar to me really helped me with that... Click above to hear the rest of Charlotte's story.",
		},
		{
			image: "Carly.png",
			name: "Carly",
			memberSince: "2025",
			quote:
				"I've really loved being part of Urban 59. The coaches are so friendly and supportive. You're not just left to your own devices, they are always there to help correct form and support you wherever you need it! They all create such a welcoming community where everyone feels encouraged. Since joining, I've started to feel much more confident and stronger in my body. It's a great environment to work out in, and I always leave feeling motivated and positive 💗",
		},
		{
			image: "Stephen.png",
			name: "Stephen",
			memberSince: "2023",
			quote:
				"The best gym experience I've ever had! I've been going since September 2023 and have been doing 6 PT sessions a month and 4 group classes a month. At 42 I'm fitter, stronger and more flexible than ever (a lot more!). Sophie is a fantastic PT with support from some super coaches.",
		},
		{
			image: "Lauren.png",
			name: "Lauren",
			memberSince: "2023",
			quote:
				"I can't recommend Urban 59 enough, the small group sessions are great for all abilities and all of the coaches are amazing, supportive and knowledgeable. It's a little fitness family, everyone is so friendly! The urban 59 community has reignited my passion for exercise, I feel energised and motivated and love our monthly challenges which keep me accountable to my goals.",
		},
		{
			image: "Laura.png",
			name: "Laura",
			memberSince: "2024",
			quote:
				"Nine months on from my original review, I can honestly say everything I felt back then still stands — if not even more so. My experience at Urban59 continues to be incredibly positive. I've built so much on my strength and fitness since joining, feeling stronger, fitter and more capable than I ever thought I would. But more than the physical changes, the biggest difference has been my confidence. The nerves and self-doubt I felt at the beginning are a world away from how I feel now. I'm more comfortable in myself, more confident in the gym, and far more willing to push myself.",
		},
		{
			image: "Jayne.png",
			name: "Jayne",
			memberSince: "2020",
			quote:
				"Joining Urban 59 has been the best thing I have ever done. I had fallen out of love with exercise but since joining a year ago it's given me confidence that I had once lost. I now train regularly & have become noticeably fit & strong. I have made some lovely friends & it's such a welcoming community. Every session is different which I love & you get so much encouragement from all of the coaches. I wished I had joined sooner!",
		},

		{
			image: "Steve.png",
			name: "Steve",
			memberSince: "2024",
			quote:
				"Urban 59. Where to start. The friendly community gym, based in Sidcup is literally awe inspiring. It's taught me a new skill I never knew existed. I've been a member now for 10 months & it's changed my life. I've become fitter, stronger & healthier & I cannot speak of Sophie & the other coaches highly enough. I write this review on my last day of a Caribbean holiday, and for the 1st time ever, I've trained in the gym on holiday 7 times...if my review inspires 1 person to join, then I'll be happy!!",
		},
	];

	const stats = loc?.stats || [
		{ number: "6", label: "people per session" },
		{ number: "100+", label: "Happy Clients" },
		{ number: "7", label: "Expert Trainers" },
		{ number: "1k+", label: "Workouts completed" },
	];

	// Testimonials can be overridden per location. A location with a non-empty
	// list uses its own; otherwise (none set, or an empty list) it falls back to
	// the default Sidcup set so every gym shows social proof.
	const displayTestimonials =
		loc?.testimonials && loc.testimonials.length > 0
			? loc.testimonials
			: testimonials;

	const nextTestimonial = () => {
		setCurrentTestimonialIndex((prev) =>
			prev === displayTestimonials.length - 3 ? 0 : prev + 1,
		);
	};

	const prevTestimonial = () => {
		setCurrentTestimonialIndex((prev) =>
			prev === 0 ? displayTestimonials.length - 3 : prev - 1,
		);
	};

	const visibleTestimonials = displayTestimonials.slice(
		currentTestimonialIndex,
		currentTestimonialIndex + 3,
	);

	return (
		<div className="min-vh-100">
			{loc && <LocationSeo location={loc} />}

			{/* Navbar */}
			<nav
				className="modern-navbar"
				role="navigation"
				aria-label="Main navigation"
			>
				<div className="modern-navbar-container">
					<Link className="modern-navbar-brand" to="/">
						<img
							src={process.env.PUBLIC_URL + "/EU59_LogoAsset 7@4x.png"}
							alt="Urban 59"
							style={{ height: "60px", width: "auto" }}
						/>
						{loc?.shortName && (
							<span className="modern-navbar-location">{loc.shortName}</span>
						)}
					</Link>

					<div className="modern-navbar-menu">
						<Link to="/" className="modern-nav-link">
							All Locations
						</Link>
						<a href="#about" className="modern-nav-link">
							About Us
						</a>
						<a href="#features" className="modern-nav-link">
							Approach
						</a>
						<a href="#testimonials" className="modern-nav-link">
							Testimonals
						</a>
						<a href="#contact" className="modern-nav-link">
							Contact
						</a>
					</div>

					<div className="modern-navbar-actions">
						{user ? (
							<>
								<span className="modern-user-text">Welcome, {user.email}</span>
								<button className="modern-btn-logout" onClick={handleLogout}>
									Logout
								</button>
							</>
						) : loc?.showLogin === false ? (
							// Login hidden for this location (no portal access yet).
							// Show the offer CTA (opens the OfferModal) instead.
							hero.offer?.options?.length > 0 && (
								<button
									className="modern-btn-trial modern-btn-cta"
									onClick={() => setShowOffer(true)}
								>
									{hero.offer.label || "Get Started"}
								</button>
							)
						) : (
							<button
								className="modern-btn-trial"
								onClick={() => setShowLogin(true)}
							>
								LOGIN
							</button>
						)}
					</div>
				</div>

				{/* Location banner (mobile only) — rendered inside the fixed navbar
				    as a second row so it always sits directly under the nav. */}
				{loc?.shortName && (
					<div className="modern-location-banner">{loc.shortName}</div>
				)}
			</nav>

			{/* Hero Section */}
			<section className="modern-hero-section" aria-label="Hero section">
				<div className="modern-hero-container">
					<div className="modern-hero-content">
						{/* Background Video */}
						<video
							autoPlay
							loop
							muted
							playsInline
							poster={
								process.env.PUBLIC_URL +
								"/" +
								(hero.poster || "WomanMattBW.jpg")
							}
							style={{
								position: "absolute",
								top: 0,
								left: 0,
								width: "100%",
								height: "100%",
								objectFit: "cover",
								zIndex: 0,
								opacity: 0.3,
								borderRadius: "inherit",
							}}
						>
							<source
								src={
									process.env.PUBLIC_URL +
									"/" +
									(hero.backgroundVideo || "landscape promo websiteLOW.mp4")
								}
								type="video/mp4"
							/>
						</video>
						<div className="modern-hero-text">
							<h1 className="modern-hero-title" itemProp="headline">
								{(
									hero.title || "Build Strength.\nFeel Confident.\nLive Better."
								)
									.split("\n")
									.map((line, i) => (
										<React.Fragment key={i}>
											{line}
											<br />
										</React.Fragment>
									))}
							</h1>
							<p className="modern-hero-description">
								{hero.description ||
									"Experience small group personal training in Sidcup designed to help you get stronger, move better, and feel more energised every day. Train with purpose, look after your body, and create habits that truly change your lifestyle."}
							</p>

							<div className="modern-hero-actions">
								{/* Optional primary CTA (e.g. Ebbsfleet "Get Started").
								    Opens the OfferModal with the purchase options. */}
								{hero.offer?.options?.length > 0 && (
									<button
										className="modern-btn-primary modern-btn-cta"
										onClick={() => setShowOffer(true)}
									>
										{hero.offer.label || "Get Started"}
										<svg
											width="20"
											height="20"
											viewBox="0 0 20 20"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												d="M7.5 15L12.5 10L7.5 5"
												stroke="currentColor"
												strokeWidth="2"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>
									</button>
								)}
								<button
									className={
										hero.offer?.options?.length > 0
											? "modern-btn-secondary"
											: "modern-btn-primary"
									}
									onClick={() =>
										document
											.getElementById("contact")
											.scrollIntoView({ behavior: "smooth" })
									}
								>
									Enquire Now
									<svg
										width="20"
										height="20"
										viewBox="0 0 20 20"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M7.5 15L12.5 10L7.5 5"
											stroke="currentColor"
											strokeWidth="2"
											strokeLinecap="round"
											strokeLinejoin="round"
										/>
									</svg>
								</button>
								{/* <button className="modern-btn-secondary">
									<svg
										width="24"
										height="24"
										viewBox="0 0 24 24"
										fill="currentColor"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path d="M8 5v14l11-7z" />
									</svg>
									Watch Video
								</button> */}
							</div>

							{/* <div className="modern-hero-social">
								<div className="modern-hero-avatars">
									<div className="modern-avatar">👤</div>
									<div className="modern-avatar">👤</div>
									<div className="modern-avatar">👤</div>
								</div>
								<div className="modern-hero-rating">
									<div className="modern-stars">★★★★★</div>
									<span className="modern-reviews">1.7k reviews</span>
								</div>
							</div> */}

							{/* <div className="modern-hero-categories">
								{categories.map((category, index) => (
									<span key={index} className="modern-category-tag">
										{category}
									</span>
								))}
							</div> */}
						</div>

						<div className="modern-hero-image">
							<img
								src={
									process.env.PUBLIC_URL + "/" + (hero.image || "Sophie01.png")
								}
								alt="Fitness trainer"
								className="modern-hero-img"
							/>
						</div>
					</div>
				</div>
			</section>

			{/* Stats Bar */}
			{/* <section className="modern-stats-bar">
				<div className="modern-stats-bar-container">
					<div className="modern-stat-box">
						<div className="modern-stat-value">85%</div>
						<div className="modern-stat-text">
							of users get results within the 6 weeks
						</div>
					</div>
					<div className="modern-stat-divider"></div>
					<div className="modern-stat-box">
						<div className="modern-stat-value">+3K</div>
						<div className="modern-stat-text">
							users already use our Urban59 App
						</div>
					</div>
					<div className="modern-stat-divider"></div>
					<div className="modern-stat-box">
						<div className="modern-stat-value">+5K</div>
						<div className="modern-stat-text">
							workouts available for all fitness levels
						</div>
					</div>
					<div className="modern-stat-divider"></div>
					<div className="modern-stat-box">
						<div className="modern-stat-value">4.8</div>
						<div className="modern-stat-text">
							average rating from thousands of happy users
						</div>
					</div>
				</div>
			</section> */}

			{/* About Section */}
			<section
				id="about"
				className="modern-about-section"
				aria-labelledby="about-heading"
			>
				<div className="container">
					<div className="modern-about-header">
						<p className="section-label">About Us</p>
						<h2 id="about-heading" className="section-title">
							<span className="highlight">Strength</span> For Life
						</h2>
						<p className="modern-about-description">
							{loc?.aboutDescription ||
								"Train alongside people just like you, looking to feel better, move better, and build real confidence. Our small group personal training in Sidcup is limited to just six people per session, giving you the attention of personal training with the energy of a group. No overcrowded classes. No getting lost in the room. Just focused coaching, a supportive environment, and a space where you can truly look after your body."}
						</p>
					</div>

					{stats.length > 0 && (
						<div className="modern-stats-grid">
							{stats.map((stat, index) => (
								<div key={index} className="modern-stat-item">
									<div className="modern-stat-number">{stat.number}</div>
									<div className="modern-stat-label">{stat.label}</div>
								</div>
							))}
						</div>
					)}
				</div>
			</section>

			{/* Features Section */}
			<section
				id="features"
				className="features-section"
				aria-labelledby="features-heading"
			>
				<div className="features-section-wrapper">
					<div className="vertical-title" aria-hidden="true">
						Why?
					</div>
					<div className="container">
						<div className="row text-center mb-5">
							<div className="col-lg-8 mx-auto">
								<p className="section-label">Our approach</p>
								<h2 id="features-heading" className="section-title">
									Why Choose <span className="highlight">Urban 59?</span>
								</h2>
								<p className="lead text-muted">
									Our unique approach to fitness combines the energy of group
									training with the personal attention you deserve.
								</p>
							</div>
						</div>

						<div
							className="features-grid-2x2"
							style={{
								display: "grid",
								gridTemplateColumns: "repeat(4, 1fr)",
								gap: "2rem 3rem",
								maxWidth: "1200px",
								margin: "0 auto",
							}}
						>
							{features.map((feature, index) => (
								<div
									key={index}
									style={{
										textAlign: "center",
										padding: "1rem",
									}}
								>
									<div
										style={{
											width: "80px",
											height: "80px",
											borderRadius: "50%",
											margin: "0 auto 1.5rem",
											border: "3px solid #ffffff",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											backgroundColor: "rgba(255, 255, 255, 0.15)",
										}}
									>
										<span
											style={{
												fontSize: "2rem",
												fontWeight: 800,
												color: "#ffffff",
											}}
										>
											{index + 1}
										</span>
									</div>
									<h4
										style={{
											color: "#ffffff",
											marginBottom: "0.25rem",
											fontSize: "1.4rem",
											fontWeight: 800,
											textTransform: "uppercase",
										}}
									>
										{feature.title}
									</h4>
									<p
										style={{
											color: "#1a1a1a",
											fontWeight: 700,
											fontSize: "1.15rem",
											marginBottom: "0.75rem",
											textTransform: "uppercase",
										}}
									>
										{feature.subtitle}
									</p>
									<p
										style={{
											color: "#ffffff",
											fontSize: "0.95rem",
											lineHeight: 1.6,
											marginBottom: 0,
										}}
									>
										{feature.description}
									</p>
								</div>
							))}
						</div>
					</div>
				</div>
			</section>

			{/* Testimonials Section (only when the location has testimonials) */}
			{displayTestimonials.length > 0 && (
				<section
					id="testimonials"
					className="testimonials-section"
					aria-labelledby="testimonials-heading"
				>
					<div className="container">
						<div className="row text-center mb-5">
							<div className="col-lg-8 mx-auto">
								<p className="section-label">Testimonials</p>
								<h2 id="testimonials-heading" className="section-title">
									What Our <span className="highlight">Members Say</span>
								</h2>
								<p className="lead">
									Real stories from real people who transformed their lives at
									Urban 59
								</p>
							</div>
						</div>

						<div className="testimonials-carousel">
							<button
								className="carousel-btn carousel-btn-prev"
								onClick={prevTestimonial}
								aria-label="Previous testimonials"
							>
								‹
							</button>

							<div className="row g-4">
								{visibleTestimonials.map((testimonial, index) => (
									<div
										key={currentTestimonialIndex + index}
										className="col-md-4"
									>
										<div className="testimonial-card">
											{testimonial.video ? (
												<button
													type="button"
													className="testimonial-image testimonial-video-trigger"
													onClick={() => setActiveVideo(testimonial)}
													aria-label={`Play ${testimonial.name}'s video testimonial`}
												>
													<img
														src={
															process.env.PUBLIC_URL + "/" + testimonial.image
														}
														alt={testimonial.name}
													/>
													<span
														className="testimonial-play-button"
														aria-hidden="true"
													>
														<svg
															width="28"
															height="28"
															viewBox="0 0 24 24"
															fill="currentColor"
															xmlns="http://www.w3.org/2000/svg"
														>
															<path d="M8 5v14l11-7z" />
														</svg>
													</span>
												</button>
											) : (
												<div className="testimonial-image">
													<img
														src={
															process.env.PUBLIC_URL + "/" + testimonial.image
														}
														alt={testimonial.name}
													/>
												</div>
											)}
											<div className="testimonial-content">
												<p className="testimonial-quote">
													"{testimonial.quote}"
												</p>
												<div>
													<p className="testimonial-author">
														{testimonial.name}
													</p>
													<div className="testimonial-member-since">
														<img
															src={
																process.env.PUBLIC_URL +
																"/EU59_IconWHTAsset 4@4x.png"
															}
															alt="Urban 59"
															className="testimonial-icon"
														/>
														<span>Member since {testimonial.memberSince}</span>
													</div>
												</div>
											</div>
										</div>
									</div>
								))}
							</div>

							<button
								className="carousel-btn carousel-btn-next"
								onClick={nextTestimonial}
								aria-label="Next testimonials"
							>
								›
							</button>
						</div>

						<div className="carousel-dots">
							{Array.from({
								length: Math.max(displayTestimonials.length - 2, 0),
							}).map((_, index) => (
								<button
									key={index}
									className={`carousel-dot ${
										index === currentTestimonialIndex ? "active" : ""
									}`}
									onClick={() => setCurrentTestimonialIndex(index)}
									aria-label={`Go to testimonial set ${index + 1}`}
								/>
							))}
						</div>
					</div>
				</section>
			)}

			{/* Timetable Section */}
			<section
				id="timetable"
				className="timetable-section"
				aria-labelledby="timetable-heading"
			>
				<div className="container">
					<div className="timetable-header">
						<p className="section-label">Schedule</p>
						<h2 id="timetable-heading" className="section-title">
							Session <span className="highlight">Schedule</span>
						</h2>
					</div>

					<div className="timetable-container">
						<table className="timetable">
							<thead>
								<tr>
									{days.map((day) => (
										<th key={day}>{day}</th>
									))}
								</tr>
							</thead>
							<tbody>
								{(() => {
									// Schedule source:
									//   static   -> use the config's fixed schedule
									//   firestore (default) -> live Firestore, falling back to the
									//                           baked-in Sidcup default
									const displaySchedule =
										loc?.timetable?.source === "static"
											? loc.timetable.schedule || {}
											: schedule || DEFAULT_SCHEDULE;
									// Group times by hour so e.g. 10:00 and 10:30 share a row
									const allTimes = [
										...new Set(
											days.flatMap((day) =>
												(displaySchedule[day] || []).map((slot) => slot.time),
											),
										),
									].sort();
									// Get unique hours in order
									const hours = [
										...new Set(allTimes.map((t) => t.split(":")[0])),
									].sort();
									return hours.map((hour) => (
										<tr key={hour}>
											{days.map((day) => {
												const slot = (displaySchedule[day] || []).find(
													(s) => s.time.split(":")[0] === hour,
												);
												if (!slot) return <td key={day}></td>;
												return (
													<td key={day} className="time-slot">
														{slot.time}
														{slot.note && "*"}
													</td>
												);
											})}
										</tr>
									));
								})()}
							</tbody>
						</table>
					</div>
				</div>
			</section>

			{/* Contact / enquiry section (config-driven by enquiryType) */}
			{enquiryType === "register-interest" ? (
				<RegisterInterestForm location={loc} />
			) : enquiryType === "enquiry" ? (
				<ContactForm variant="enquiry" location={loc} />
			) : (
				<ContactForm formId={loc?.closerFormId} />
			)}

			{/* Footer */}
			<Footer location={loc} />

			{/* Scroll to Top Button */}
			{showScrollTop && (
				<button
					className="scroll-to-top"
					onClick={scrollToTop}
					aria-label="Scroll to top"
				>
					↑
				</button>
			)}

			{/* Login Modal */}
			<LoginModal show={showLogin} onClose={() => setShowLogin(false)} />

			{/* Offer Modal (purchase options) */}
			{showOffer && hero.offer && (
				<OfferModal offer={hero.offer} onClose={() => setShowOffer(false)} />
			)}

			{/* Video Testimonial Modal */}
			{activeVideo && (
				<VideoTestimonialModal
					testimonial={activeVideo}
					onClose={() => setActiveVideo(null)}
				/>
			)}
		</div>
	);
};

// Video Testimonial Modal Component
const VideoTestimonialModal = ({ testimonial, onClose }) => {
	const videoRef = React.useRef(null);

	// Close on Escape key and lock body scroll while open
	useEffect(() => {
		const videoEl = videoRef.current;
		const handleKeyDown = (e) => {
			if (e.key === "Escape") onClose();
		};
		document.addEventListener("keydown", handleKeyDown);
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.removeEventListener("keydown", handleKeyDown);
			document.body.style.overflow = previousOverflow;
			// Pause playback on close to stop audio continuing in background
			if (videoEl) {
				videoEl.pause();
			}
		};
	}, [onClose]);

	return (
		<div
			className="video-modal-backdrop"
			onClick={onClose}
			role="dialog"
			aria-modal="true"
			aria-label={`${testimonial.name}'s video testimonial`}
		>
			<div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
				<button
					type="button"
					className="video-modal-close"
					onClick={onClose}
					aria-label="Close video"
				>
					×
				</button>
				<video
					ref={videoRef}
					className="video-modal-player"
					src={process.env.PUBLIC_URL + "/" + testimonial.video}
					poster={process.env.PUBLIC_URL + "/" + testimonial.image}
					controls
					autoPlay
					playsInline
				/>
			</div>
		</div>
	);
};

// Coming Soon Modal Component
const ComingSoonModal = ({ onClose }) => {
	return (
		<div
			className="modal show d-block"
			style={{
				backgroundColor: "rgba(0,0,0,0.7)",
				backdropFilter: "blur(5px)",
			}}
		>
			<div className="modal-dialog modal-dialog-centered">
				<div
					className="modal-content"
					style={{
						boxShadow: "0 8px 32px 0 rgba(0,0,0,0.45)",
						background: "#2d1b12",
						color: "#ffffff",
					}}
				>
					<div className="modal-header border-0 position-relative">
						<div className="w-100 text-center mb-2 d-flex flex-column align-items-center">
							<img
								src={process.env.PUBLIC_URL + "/EU59_LogoWHTAsset 5@4x.png"}
								alt="Urban 59 Logo"
								style={{ maxWidth: "120px", height: "auto" }}
							/>
						</div>
					</div>

					<div
						className="modal-body text-center"
						style={{ padding: "3rem 2rem" }}
					>
						<h2
							style={{
								fontSize: "2.5rem",
								fontWeight: "800",
								textTransform: "uppercase",
								letterSpacing: "1px",
								marginBottom: "1.5rem",
								color: "#ffffff",
							}}
						>
							Coming Soon
						</h2>
						<p
							style={{
								fontSize: "1.1rem",
								color: "rgba(255, 255, 255, 0.8)",
								marginBottom: "2rem",
								lineHeight: "1.6",
							}}
						>
							Our member portal is currently under development. Check back soon
							for access to your personalized fitness dashboard!
						</p>
						<button
							type="button"
							className="btn w-100"
							onClick={onClose}
							style={{
								background: "#e85d3f",
								color: "#ffffff",
								border: "none",
								padding: "0.75rem",
								fontWeight: "600",
								borderRadius: "8px",
								fontSize: "1rem",
							}}
						>
							Close
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default LandingPage;
